import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { toast } from "react-toastify";
import EntitySearchPicker, {
  getEntityDisplayName,
  getEntityId,
} from "../components/EntitySearchPicker";
import StepBar from "../components/StepBar";
import {
  loadAlertRuleLookups,
  loadDevicesTable,
  loadEntityAddressItems,
} from "../customer/onboardingApi";
import crudsService from "services/cruds-service";
import { InputAdornment, TextField } from "@mui/material";

// ── Steps ─────────────────────────────────────────────────────
const STEPS = [
  { key: "search", label: "Find Entity" },
  { key: "select-device", label: "Device" },
  { key: "manage", label: "Notifications" },
];

const BLANK_RULE = {
  alert_type_id: 0,
  alert_sub_type_id: 0,
  frequency: "",
  event_type_id: 0,
  event_type_name: "",
  send_to_type_id: 3,
  contact_id: 0,
  address_id: 0,
  contact_label: "",
};

const FREQUENCY_OPTIONS = [
  { value: "60", label: "Every 1 hour" },
  { value: "120", label: "Every 2 hours" },
  { value: "180", label: "Every 3 hours" },
  { value: "240", label: "Every 4 hours" },
  { value: "360", label: "Every 6 hours" },
  { value: "480", label: "Every 8 hours" },
  { value: "720", label: "Every 12 hours" },
  { value: "1440", label: "Every 24 hours" },
];

const etId = (et) => et.event_type_id ?? et.id ?? 0;

// Auto-select template IDs per spec — never exposed to users
function resolveTemplates(alertTypeName, subTypeName, isSms) {
  const isEmptied = alertTypeName.toLowerCase().includes("emptied");
  const isHeal = subTypeName.toLowerCase() === "heal";
  if (isSms) return { message_template_id: isEmptied ? 15 : isHeal ? 13 : 4 };
  return { template_id: isEmptied ? 27 : isHeal ? 20 : 21 };
}

export default function NotificationWizard() {
  // ── Main wizard state
  const [step, setStep] = useState("search");
  const [selectedEntity, setSelectedEntity] = useState(null);

  // ── Device selection
  const [entityDevices, setEntityDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);

  // ── Alert rules
  const [rules, setRules] = useState([]);
  const [loadingRules, setLoadingRules] = useState(false);
  const [alertEnabled, setAlertEnabled] = useState(true);
  const [togglingAlert, setTogglingAlert] = useState(false);

  // ── Lookup data (loaded once per selected device)
  const [lookups, setLookups] = useState(null);
  const [loadingLookups, setLoadingLookups] = useState(false);

  // ── Rule form
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [ruleFormStep, setRuleFormStep] = useState("alert");
  const [ruleForm, setRuleForm] = useState(BLANK_RULE);
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [savingRule, setSavingRule] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [deviceFilter, setDeviceFilter] = useState("");

  // ── Load devices for entity ───────────────────────────────
  const loadDevicesForEntity = useCallback(async (entityId) => {
    setLoadingDevices(true);
    try {
      const [itemsRes, devicesRes] = await Promise.all([
        loadEntityAddressItems(entityId),
        loadDevicesTable(),
      ]);
      const raw = itemsRes.data?.data || itemsRes.data || itemsRes;
      const items = (Array.isArray(raw) ? raw : []).map((i) => ({
        id: i.address_item_id ?? i.entity_address_item_id ?? i.id,
        name: i.name ?? i.item_name ?? `Item ${i.address_item_id ?? i.id}`,
      }));
      const itemIds = new Set(items.map((i) => i.id));
      const nameById = new Map(items.map((i) => [i.id, i.name]));

      const devs = devicesRes
        .filter(
          (d) => d.address_item_id != null && itemIds.has(d.address_item_id),
        )
        .map((d) => ({
          device_id: d.device_id,
          device_name: d.device_name,
          serial_number: d.serial_number,
          address_item_id: d.address_item_id ?? undefined,
          item_name: d.address_item_id
            ? nameById.get(d.address_item_id)
            : undefined,
          status: d.status,
        }));

      setEntityDevices(devs);
    } catch {
      toast.error("Failed to load devices for this entity");
    } finally {
      setLoadingDevices(false);
    }
  }, []);

  // ── Load rules ─────────────────────────────────────────────
  const loadRules = useCallback(async (deviceId) => {
    setLoadingRules(true);
    try {
      // const res = await loadAlertRulesForDevice(deviceId);
      const res = await crudsService.getAlertRules({ id: deviceId });
      const raw = res.data || res;
      const filterRaw = raw.map((e) => e.attributes);
      setRules(Array.isArray(filterRaw) ? filterRaw : []);
    } catch {
      toast.error("Failed to load alert rules");
    } finally {
      setLoadingRules(false);
    }
  }, []);

  // ── Fetch devices when entity changes ─────────────────────
  useEffect(() => {
    if (!selectedEntity) {
      setEntityDevices([]);
      return;
    }
    loadDevicesForEntity(getEntityId(selectedEntity));
  }, [selectedEntity, loadDevicesForEntity]);

  // ── Fetch rules + lookups when device changes ─────────────
  useEffect(() => {
    if (!selectedDevice) return;
    loadRules(selectedDevice.device_id);
    setLoadingLookups(true);
    loadAlertRuleLookups(selectedDevice.device_id)
      .then((d) => setLookups(d))
      .catch(() => toast.error("Failed to load notification options"))
      .finally(() => setLoadingLookups(false));
  }, [selectedDevice, loadRules]);

  // ── Master toggle ──────────────────────────────────────────
  const handleToggleAlerts = async () => {
    if (!selectedDevice) return;
    setTogglingAlert(true);
    try {
      await crudsService.setDeviceAlertEnable({
        deviceId: selectedDevice.device_id,
        is_notified: !alertEnabled,
      });
      setAlertEnabled((p) => !p);
      toast.success(`Alerts ${!alertEnabled ? "enabled" : "disabled"}`);
    } catch {
      toast.error("Failed to update alert setting");
    } finally {
      setTogglingAlert(false);
    }
  };

  // ── Open form helpers ─────────────────────────────────────
  const openAddForm = () => {
    setRuleForm(BLANK_RULE);
    setEditingRuleId(null);
    setRuleFormStep("alert");
    setShowRuleForm(true);
  };

  const openEditForm = (rule) => {
    setRuleForm({
      alert_type_id: rule.alert_type_id,
      alert_sub_type_id: rule.alert_sub_type_id,
      frequency: rule.frequency ?? "",
      event_type_id: rule.event_type_id,
      event_type_name: rule.EventType?.name ?? "",
      send_to_type_id: rule.send_to_type_id,
      contact_id: 0,
      address_id: rule.address_id,
      contact_label: rule.ContactAddress?.contact_address ?? "",
    });
    setEditingRuleId(rule.rule_id);
    setRuleFormStep("alert");
    setShowRuleForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ── Delete rule ────────────────────────────────────────────
  const handleDeleteRule = async (ruleId) => {
    try {
      await crudsService.deleteAlertRule(ruleId);
      setRules((prev) => prev.filter((r) => r.rule_id !== ruleId));
      setDeleteConfirm(null);
      toast.success("Rule deleted");
    } catch {
      toast.error("Failed to delete rule");
    }
  };

  // ── Derived form state ────────────────────────────────────
  const filteredSubTypes = (lookups?.subTypes ?? []).filter(
    (st) => st.alert_type_id === ruleForm.alert_type_id,
  );
  const selectedSubTypeName =
    (lookups?.subTypes ?? []).find(
      (st) => st.alert_sub_type_id === ruleForm.alert_sub_type_id,
    )?.sub_type_name ?? "";
  const isResent = selectedSubTypeName.toLowerCase() === "resent";
  const isSms = ruleForm.event_type_name === "send_sms";

  const addressTypeObj = {
    send_sms: 1,
    send_email: 2,
    send_push: 3,
  };

  const filteredContacts = (lookups?.entityContacts ?? []).filter((c) => {
    if (!c.ContactAddress) return false;
    return (
      c.ContactAddress.address_type_id ===
      addressTypeObj[ruleForm.event_type_name]
    );
  });

  // ── Save rule ─────────────────────────────────────────────
  const handleSaveRule = async () => {
    if (!selectedDevice || !lookups) return;
    const alertType = lookups.alertTypes.find(
      (a) => a.alert_type_id === ruleForm.alert_type_id,
    );
    const subType = lookups.subTypes.find(
      (s) => s.alert_sub_type_id === ruleForm.alert_sub_type_id,
    );

    const payload = {
      alert_type_id: ruleForm.alert_type_id,
      alert_sub_type_id: ruleForm.alert_sub_type_id,
      event_type_id: ruleForm.event_type_id,
      send_to_type_id: ruleForm.send_to_type_id,
      address_id: ruleForm.address_id,
      status_type_id: lookups.activeStatusTypeId,
      device_id: String(selectedDevice.device_id),
      module_id: 12,
      frequency: isResent && ruleForm.frequency ? ruleForm.frequency : null,
      ...resolveTemplates(
        alertType?.type_name ?? "",
        subType?.sub_type_name ?? "",
        isSms,
      ),
    };
    setSavingRule(true);
    try {
      if (editingRuleId) {
        await crudsService.editAlertRule(payload, editingRuleId);
        toast.success("Rule updated");
      } else {
        await crudsService.createAlertRule(payload);
        toast.success("Rule created");
      }
      setShowRuleForm(false);
      setRuleForm(BLANK_RULE);
      setEditingRuleId(null);
      await loadRules(selectedDevice.device_id);
    } catch (err) {
      toast.error(err.message || "Failed to save rule");
    } finally {
      setSavingRule(false);
    }
  };

  // ── Step validation ───────────────────────────────────────
  const canAdvanceAlert =
    ruleForm.alert_type_id > 0 &&
    (filteredSubTypes.length === 0 || ruleForm.alert_sub_type_id > 0) &&
    (!isResent || ruleForm.frequency !== "");
  const canAdvanceMethod = ruleForm.event_type_id > 0;
  const canSave = ruleForm.address_id > 0;

  // ── Group rules by alert type name ────────────────────────
  const groupedRules = rules.reduce((acc, rule) => {
    const key = rule.AlertType?.type_name ?? `Type ${rule.alert_type_id}`;
    (acc[key] ??= []).push(rule);
    return acc;
  }, {});

  const filteredDevices = entityDevices.filter((d) => {
    if (!deviceFilter) return true;
    const f = deviceFilter.toLowerCase();
    return (
      d.device_name.toLowerCase().includes(f) ||
      (d.serial_number ?? "").toLowerCase().includes(f)
      // (d.external_device_id ?? '').toLowerCase().includes(f) ||
      // (d.device_type_name ?? '').toLowerCase().includes(f) ||
      // (d.brand_name ?? '').toLowerCase().includes(f) ||
      // (d.model_name ?? '').toLowerCase().includes(f) ||
      // (d.map_identifier ?? '').toLowerCase().includes(f) ||
      // (d.item_name ?? '').toLowerCase().includes(f) ||
      // (d.entity_address_item_name ?? '').toLowerCase().includes(f)
    );
  });

  return (
    <MDBox sx={{ mx: "auto" }}>
      {step !== "done" && <StepBar current={step} STEPS={STEPS} />}

      {/* ── Step 1: Search entity ── */}
      {step === "search" && (
        <MDBox
          sx={{
            backgroundColor: "rgb(38, 40, 51)",
            border: "1px solid gray",
            borderRadius: "12px",
            p: 3,
          }}
        >
          <MDTypography variant="h6" color="white" fontWeight="bold">
            Find the Entity
          </MDTypography>
          <MDTypography
            variant="button"
            color="white"
            opacity={0.6}
            sx={{ display: "block", mb: 3 }}
          >
            Search for the customer or entity you want to add an address to.
          </MDTypography>

          <EntitySearchPicker
            selectedEntity={selectedEntity}
            onSelect={setSelectedEntity}
          />

          <MDBox mt={4} display="flex" justifyContent="flex-end">
            <MDButton
              disabled={!selectedEntity}
              onClick={() => setStep("select-device")}
              variant="gradient"
              color="info"
              sx={{ gap: 1 }}
            >
              Continue <i className="fa fa-chevron-right" />
            </MDButton>
          </MDBox>
        </MDBox>
      )}

      {/* ── Step 2: Select device ── */}
      {step === "select-device" && selectedEntity && (
        <MDBox
          sx={{
            backgroundColor: "rgb(38, 40, 51)",
            border: "1px solid gray",
            borderRadius: "12px",
            p: 3,
          }}
        >
          <MDTypography
            variant="h6"
            color="white"
            fontWeight="bold"
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <i className="fa fa-map-marker-alt" style={{ color: "#FACC15" }} />{" "}
            Select Device
          </MDTypography>
          <MDTypography
            variant="button"
            color="white"
            opacity={0.6}
            sx={{ display: "block", mb: 3 }}
          >
            Devices assigned to:{" "}
            <span style={{ color: "white", fontWeight: 500 }}>
              {selectedEntity ? getEntityDisplayName(selectedEntity) : ""}
            </span>
          </MDTypography>

          <TextField
            fullWidth
            size="small"
            value={deviceFilter}
            onChange={(e) => setDeviceFilter(e.target.value)}
            placeholder="Search by name or serial…"
            sx={{ my: 2, input: { color: "white" } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <i className="fas fa-search" style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />

          {loadingDevices ? (
            <MDBox
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                color: "#9CA3AF",
                py: 5,
              }}
            >
              <i className="fa fa-spinner fa-spin" />
              <MDTypography variant="body2" color="inherit">
                Loading devices…
              </MDTypography>
            </MDBox>
          ) : entityDevices.length === 0 ? (
            <MDBox sx={{ textAlign: "center", py: 5 }}>
              <MDBox sx={{ mb: 2 }}>
                <i
                  className="fa fa-microchip"
                  style={{ fontSize: 28, color: "#4B5563" }}
                />
              </MDBox>

              <MDTypography variant="body2" sx={{ color: "#9CA3AF", mb: 1 }}>
                No devices are assigned to this entity yet.
              </MDTypography>

              <Link
                href="/erp/onboarding/assign-device"
                style={{
                  color: "#4ADE80",
                  fontSize: "0.875rem",
                  textDecoration: "none",
                }}
              >
                Assign a device first →
              </Link>
            </MDBox>
          ) : (
            <MDBox
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                height: "50vh",
                overflowY: "scroll",
              }}
            >
              {filteredDevices.map((device) => {
                const isSelected =
                  selectedDevice?.device_id === device.device_id;

                return (
                  <MDBox
                    key={device.device_id}
                    component="button"
                    type="button"
                    onClick={() => setSelectedDevice(device)}
                    sx={{
                      width: "100%",
                      textAlign: "left",
                      px: 2,
                      py: 1.5,
                      borderRadius: "10px",
                      border: "1px solid",
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      transition: "all 0.2s",
                      background: "transparent",
                      borderColor: isSelected ? "#49a3f1" : "gray",
                      cursor: "pointer",
                      "&:hover": {
                        borderColor: "#49a3f1",
                      },
                    }}
                  >
                    <MDBox
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "8px",
                        background: "#1A2B1A",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <i
                        className="fa fa-microchip"
                        style={{
                          color: isSelected ? "#49a3f1" : "gray",
                        }}
                      />
                    </MDBox>

                    <MDBox sx={{ flex: 1, minWidth: 0 }}>
                      <MDTypography
                        variant="button"
                        fontWeight="medium"
                        sx={{ color: "white" }}
                      >
                        {device.device_name} | {device.device_id}
                      </MDTypography>

                      <MDTypography
                        variant="caption"
                        sx={{ color: "white", display: "block" }}
                      >
                        {device.serial_number
                          ? `S/N: ${device.serial_number}`
                          : ""}
                        {device.serial_number && device.item_name ? " · " : ""}
                        {device.item_name ?? ""}
                      </MDTypography>
                    </MDBox>

                    {isSelected && (
                      <i
                        className="fa fa-check-circle"
                        style={{ color: "#4ADE80", flexShrink: 0 }}
                      />
                    )}
                  </MDBox>
                );
              })}
            </MDBox>
          )}

          {/* Nav */}
          <MDBox mt={4} display="flex" justifyContent="space-between">
            <MDButton
              onClick={() => setStep("search")}
              variant="outlined"
              color="secondary"
              sx={{ gap: 1 }}
            >
              <i className="fa fa-chevron-left" /> Back
            </MDButton>
            <MDButton
              disabled={!selectedDevice}
              onClick={() => setStep("manage")}
              variant="gradient"
              color="info"
              sx={{ gap: 1 }}
            >
              Manage Rules <i className="fa fa-chevron-right" />
            </MDButton>
          </MDBox>
        </MDBox>
      )}

      {/* ── Step 3: Manage rules ── */}
      {step === "manage" && selectedDevice && (
        <MDBox
          sx={{
            backgroundColor: "rgb(38, 40, 51)",
            border: "1px solid gray",
            borderRadius: "12px",
            p: 3,
          }}
        >
          <MDBox
            sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}
          >
            <MDBox
              sx={{
                width: 40,
                height: 40,
                borderRadius: "12px",
                background: "#1A2B1A",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <i className="fa fa-microchip" style={{ color: "#49a3f1" }} />
            </MDBox>

            <MDBox sx={{ minWidth: 0 }}>
              <MDTypography
                variant="button"
                fontWeight="bold"
                sx={{ color: "white" }}
              >
                {selectedDevice.device_name} | {selectedDevice.device_id}
              </MDTypography>

              <MDTypography
                variant="caption"
                sx={{ color: "#fff", display: "block" }}
              >
                {selectedDevice.serial_number
                  ? `S/N: ${selectedDevice.serial_number}`
                  : ""}
                {selectedDevice.item_name
                  ? ` · ${selectedDevice.item_name}`
                  : ""}
              </MDTypography>
            </MDBox>
          </MDBox>

          <MDBox sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <MDTypography
              variant="caption"
              fontWeight="medium"
              sx={{ color: alertEnabled ? "#49a3f1" : "gray" }}
            >
              Alerts {alertEnabled ? "On" : "Off"}
            </MDTypography>

            <MDBox
              component="button"
              type="button"
              onClick={handleToggleAlerts}
              disabled={togglingAlert}
              sx={{
                fontSize: 24,
                lineHeight: 1,
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
            >
              {togglingAlert ? (
                <i
                  className="fa fa-spinner fa-spin"
                  style={{ color: "#9CA3AF", fontSize: 20 }}
                />
              ) : alertEnabled ? (
                <i className="fa fa-toggle-on" style={{ color: "#49a3f1" }} />
              ) : (
                <i className="fa fa-toggle-off" style={{ color: "#6B7280" }} />
              )}
            </MDBox>
          </MDBox>

          {/* Add Rule button row */}
          {!showRuleForm && (
            <MDBox
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <MDTypography variant="body2" sx={{ color: "#fff" }}>
                {rules.length} rule{rules.length !== 1 ? "s" : ""} configured
              </MDTypography>

              <MDBox
                component="button"
                type="button"
                onClick={openAddForm}
                disabled={loadingLookups}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  background: "#49a3f1",
                  color: "#fff",
                  fontWeight: 600,
                  px: 2,
                  py: 1,
                  borderRadius: "8px",
                  border: "none",
                  fontSize: "0.875rem",
                  cursor: "pointer",
                  transition: "0.2s",
                  marginBottom: "5px",
                  "&:hover": { background: "gray" },
                  "&:disabled": { opacity: 0.5 },
                }}
              >
                {loadingLookups ? (
                  <i
                    className="fa fa-spinner fa-spin"
                    style={{ fontSize: 12 }}
                  />
                ) : (
                  <i className="fa fa-plus" style={{ fontSize: 12 }} />
                )}
                Add Rule
              </MDBox>
            </MDBox>
          )}

          {/* Rule form */}
          {showRuleForm && lookups && (
            <MDBox
              sx={{
                border: "1px solid #49a3f1",
                borderRadius: "12px",
                p: 2.5,
                marginBottom: "10px",
              }}
            >
              {/* Form header */}
              <MDBox
                sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}
              >
                <MDBox>
                  <MDTypography
                    variant="button"
                    fontWeight="bold"
                    sx={{ color: "white" }}
                  >
                    {editingRuleId ? "Edit Rule" : "New Notification Rule"}
                  </MDTypography>

                  <MDTypography
                    variant="caption"
                    sx={{ color: "#fff", display: "block" }}
                  >
                    Step{" "}
                    {ruleFormStep === "alert"
                      ? 1
                      : ruleFormStep === "method"
                      ? 2
                      : 3}{" "}
                    of 3 —
                    {ruleFormStep === "alert"
                      ? " Alert Settings"
                      : ruleFormStep === "method"
                      ? " Delivery Method"
                      : " Recipient"}
                  </MDTypography>
                </MDBox>

                <MDBox
                  component="button"
                  onClick={() => {
                    setShowRuleForm(false);
                    setEditingRuleId(null);
                    setRuleForm(BLANK_RULE);
                  }}
                  sx={{
                    border: "none",
                    background: "none",
                    color: "#6B7280",
                    cursor: "pointer",
                    "&:hover": { color: "white" },
                  }}
                >
                  <i className="fa fa-times" />
                </MDBox>
              </MDBox>

              {/* STEP 1 */}
              {ruleFormStep === "alert" && (
                <MDBox
                  sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                >
                  <MDBox>
                    <MDTypography variant="caption" sx={{ color: "#D1D5DB" }}>
                      Alert Type <span style={{ color: "#F87171" }}>*</span>
                    </MDTypography>

                    <select
                      value={ruleForm.alert_type_id}
                      onChange={(e) => {
                        const typeId = +e.target.value;
                        const subs = lookups.subTypes.filter(
                          (st) => st.alert_type_id === typeId,
                        );

                        setRuleForm((p) => ({
                          ...p,
                          alert_type_id: typeId,
                          alert_sub_type_id:
                            subs.length === 1 ? subs[0].alert_sub_type_id : 0,
                          frequency: "",
                        }));
                      }}
                      style={{
                        width: "100%",
                        padding: "8px",
                        background: "#262833",
                        border: "1px solid #49a3f1",
                        borderRadius: 8,
                        color: "white",
                      }}
                    >
                      <option value={0}>Select alert type…</option>
                      {lookups.alertTypes.map((t) => (
                        <option key={t.alert_type_id} value={t.alert_type_id}>
                          {t.type_name}
                        </option>
                      ))}
                    </select>
                  </MDBox>

                  {ruleForm.alert_type_id > 0 &&
                    filteredSubTypes.length > 0 && (
                      <MDBox>
                        <MDTypography
                          variant="caption"
                          sx={{ color: "#fff", mb: 1 }}
                        >
                          When to Notify
                        </MDTypography>

                        <MDBox
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 1,
                          }}
                        >
                          {filteredSubTypes.map((st) => {
                            const selected =
                              ruleForm.alert_sub_type_id ===
                              st.alert_sub_type_id;

                            return (
                              <MDBox
                                key={st.alert_sub_type_id}
                                component="button"
                                onClick={() =>
                                  setRuleForm((p) => ({
                                    ...p,
                                    alert_sub_type_id: st.alert_sub_type_id,
                                    frequency: "",
                                  }))
                                }
                                sx={{
                                  px: 2,
                                  py: 1.5,
                                  borderRadius: "8px",
                                  border: "1px solid",
                                  textAlign: "left",
                                  fontSize: 13,
                                  cursor: "pointer",
                                  background: "transparent",
                                  borderColor: selected ? "#49a3f1" : "gray",
                                  color: "#fff !important",
                                }}
                              >
                                {st.sub_type_name}
                              </MDBox>
                            );
                          })}
                        </MDBox>
                      </MDBox>
                    )}

                  {isResent && (
                    <MDBox>
                      <MDTypography
                        variant="caption"
                        sx={{
                          color: "#fff",
                          fontWeight: 500,
                          mb: 1.5,
                          display: "block",
                          fontSize: "0.9rem",
                        }}
                        component="label"
                      >
                        Reminder Frequency{" "}
                        <span style={{ color: "#f87171" }}>*</span>
                      </MDTypography>
                      <select
                        value={ruleForm.frequency}
                        onChange={(e) =>
                          setRuleForm((p) => ({
                            ...p,
                            frequency: e.target.value,
                          }))
                        }
                        style={{
                          width: "100%",
                          padding: "8px",
                          background: "#262833",
                          border: "1px solid #49a3f1",
                          borderRadius: 8,
                          color: "white",
                        }}
                      >
                        <option value="">Select frequency…</option>
                        {FREQUENCY_OPTIONS.map((f) => (
                          <option key={f.value} value={f.value}>
                            {f.label}
                          </option>
                        ))}
                      </select>
                    </MDBox>
                  )}

                  <MDBox sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <MDBox
                      component="button"
                      disabled={!canAdvanceAlert}
                      onClick={() => setRuleFormStep("method")}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        background: "#49a3f1",
                        color: "#fff",
                        px: 3,
                        py: 1,
                        borderRadius: "8px",
                        border: "none",
                        cursor: "pointer",
                        "&:disabled": { opacity: 0.4 },
                      }}
                    >
                      Next <i className="fa fa-chevron-right" />
                    </MDBox>
                  </MDBox>
                </MDBox>
              )}

              {/* STEP 2 */}
              {ruleFormStep === "method" && (
                <MDBox
                  sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                >
                  <MDTypography variant="caption" sx={{ color: "#fff" }}>
                    Delivery Method
                  </MDTypography>

                  <MDBox
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {lookups.eventTypes.map((et) => {
                      const eid = etId(et);
                      const selected = ruleForm.event_type_id === eid;
                      const isSms = et.name === "send_sms";

                      return (
                        <MDBox
                          key={eid}
                          component="button"
                          onClick={() =>
                            setRuleForm((p) => ({
                              ...p,
                              event_type_id: eid,
                              event_type_name: et.name,
                              contact_id: 0,
                              address_id: 0,
                              contact_label: "",
                            }))
                          }
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 1,
                            py: 3,
                            borderRadius: "12px",
                            border: "1px solid",
                            cursor: "pointer",
                            borderColor: selected ? "#49a3f1" : "gray",
                            color: "#fff !important",
                          }}
                        >
                          <i
                            className={`fa ${
                              isSms ? "fa-comment-alt" : "fa-envelope"
                            }`}
                            style={{
                              fontSize: 20,
                              color: selected ? "#49a3f1" : "#fff",
                            }}
                          />

                          <MDTypography
                            variant="caption"
                            sx={{ color: "#fff" }}
                          >
                            {et.description || (isSms ? "SMS Text" : "Email")}
                          </MDTypography>
                        </MDBox>
                      );
                    })}
                  </MDBox>

                  <MDBox
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <MDBox
                      component="button"
                      onClick={() => setRuleFormStep("alert")}
                      sx={{
                        border: "1px solid #49a3f1",
                        px: 2,
                        py: 1,
                        borderRadius: "8px",
                        color: "#D1D5DB",
                        background: "none",
                      }}
                    >
                      <i className="fa fa-chevron-left" /> Back
                    </MDBox>

                    <MDBox
                      component="button"
                      disabled={!canAdvanceMethod}
                      onClick={() => setRuleFormStep("recipient")}
                      sx={{
                        background: "#49a3f1",
                        color: "#fff",
                        px: 3,
                        py: 1,
                        borderRadius: "8px",
                        border: "none",
                      }}
                    >
                      Next <i className="fa fa-chevron-right" />
                    </MDBox>
                  </MDBox>
                </MDBox>
              )}

              {/* STEP 3 */}
              {ruleFormStep === "recipient" && (
                <MDBox
                  sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                >
                  {filteredContacts.length === 0 ? (
                    <MDBox
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 2,
                        py: 2,
                        borderRadius: "8px",
                        border: "1px solid #49a3f1",
                        color: "#9CA3AF",
                      }}
                    >
                      <i
                        className="fa fa-exclamation-triangle"
                        style={{ color: "#49a3f1" }}
                      />
                      No contacts available
                    </MDBox>
                  ) : (
                    filteredContacts.map((c) => {
                      const addr = c.ContactAddress;
                      const selected =
                        ruleForm.address_id === addr.contact_address_id;

                      return (
                        <MDBox
                          key={addr.contact_address_id}
                          component="button"
                          onClick={() =>
                            setRuleForm((p) => ({
                              ...p,
                              contact_id: c.contact_id,
                              address_id: addr.contact_address_id,
                              contact_label: addr.contact_address,
                            }))
                          }
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            px: 2,
                            py: 1.5,
                            borderRadius: "8px",
                            border: "1px solid",
                            cursor: "pointer",
                            borderColor: selected ? "#49a3f1" : "gray",
                          }}
                        >
                          <i
                            className="fa fa-envelope"
                            style={{ color: selected ? "#fff" : "gray" }}
                          />

                          <MDBox sx={{ flex: 1 }}>
                            <MDTypography
                              variant="button"
                              sx={{ color: "#fff" }}
                            >
                              {c.first_name} {c.last_name}
                            </MDTypography>

                            <MDTypography
                              variant="caption"
                              sx={{ color: "#fff" }}
                            >
                              {addr.contact_address}
                            </MDTypography>
                          </MDBox>

                          {selected && (
                            <i
                              className="fa fa-check-circle"
                              style={{ color: "#fff" }}
                            />
                          )}
                        </MDBox>
                      );
                    })
                  )}

                  <MDBox
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <MDBox
                      component="button"
                      onClick={() => setRuleFormStep("method")}
                      sx={{
                        border: "1px solid #49a3f1",
                        px: 2,
                        py: 1,
                        borderRadius: "8px",
                        color: "#D1D5DB",
                        background: "none",
                      }}
                    >
                      <i className="fa fa-chevron-left" /> Back
                    </MDBox>

                    <MDBox
                      component="button"
                      disabled={!canSave || savingRule}
                      onClick={handleSaveRule}
                      sx={{
                        background: "#49a3f1",
                        color: "#fff",
                        px: 3,
                        py: 1,
                        borderRadius: "8px",
                        border: "none",
                      }}
                    >
                      {savingRule ? (
                        <>
                          <i className="fa fa-spinner fa-spin" /> Saving…
                        </>
                      ) : editingRuleId ? (
                        "Update Rule"
                      ) : (
                        "Save Rule"
                      )}
                    </MDBox>
                  </MDBox>
                </MDBox>
              )}
            </MDBox>
          )}

          {/* Existing rules list */}
          {loadingRules ? (
            <MDBox sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <i
                className="fa fa-spinner fa-spin"
                style={{ color: "#49a3f1", fontSize: 20 }}
              />
            </MDBox>
          ) : rules.length === 0 && !showRuleForm ? (
            <MDBox
              sx={{
                background: "transparent",
                border: "1px solid #49a3f1",
                borderRadius: "12px",
                p: 5,
                textAlign: "center",
              }}
            >
              <i
                className="fa fa-bell"
                style={{ color: "gray", fontSize: 28 }}
              />

              <MDTypography variant="body2" sx={{ color: "#fff", mt: 1 }}>
                No notification rules configured yet.
              </MDTypography>
            </MDBox>
          ) : (
            Object.entries(groupedRules).map(([typeName, typeRules]) => (
              <MDBox
                key={typeName}
                sx={{
                  background: "transparent",
                  border: "1px solid #49a3f1",
                  borderRadius: "12px",
                  overflow: "hidden",
                  mb: 2
                }}
              >
                <MDBox
                  sx={{
                    px: 2,
                    py: 1.5,
                    background: "transparent",
                    borderBottom: "1px solid #49a3f1",
                  }}
                >
                  <MDTypography
                    variant="button"
                    sx={{
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <i className="fa fa-bell" style={{ color: "gray" }} />
                    {typeName}
                  </MDTypography>
                </MDBox>

                {typeRules.map((rule) => (
                  <MDBox
                    key={rule.rule_id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      px: 2,
                      py: 1.5,
                      borderTop: "1px solid #1A2B1A",
                    }}
                  >
                    <i
                      className={`fa ${
                        rule.EventType?.name === "send_sms"
                          ? "fa-comment-alt"
                          : "fa-envelope"
                      }`}
                      style={{ color: "#49a3f1" }}
                    />

                    <MDBox sx={{ flex: 1 }}>
                      <MDTypography variant="button" sx={{ color: "white" }}>
                        {rule.AlertSubType?.sub_type_name ?? "—"}
                      </MDTypography>

                      <MDTypography
                        variant="caption"
                        sx={{ color: "#fff", display: "block" }}
                      >
                        {rule.Contact?.first_name} {rule.Contact?.last_name} ·{" "}
                        {rule.ContactAddress?.contact_address}
                      </MDTypography>
                    </MDBox>

                    <MDBox sx={{ display: "flex", gap: 1 }}>
                      <MDBox
                        component="button"
                        onClick={() => openEditForm(rule)}
                        sx={{
                          background: "none",
                          border: "none",
                          color: "#9CA3AF",
                        }}
                      >
                        <i className="fa fa-edit" />
                      </MDBox>
                      {deleteConfirm === rule.rule_id ? (
                        <MDBox
                          sx={{
                            display: "flex",
                            alignItem: "center",
                            gap: 1,
                            ml: 1,
                          }}
                        >
                          <MDButton
                            onClick={() => handleDeleteRule(rule.rule_id)}
                            sx={{
                              background: "red",
                              color: "#fff",
                            }}
                          >
                            Confirm
                          </MDButton>
                          <MDButton
                            onClick={() => setDeleteConfirm(null)}
                            sx={{
                              background: "#284D2D",
                              color: "#fff",
                            }}
                          >
                            Cancel
                          </MDButton>
                        </MDBox>
                      ) : (
                        <MDBox
                          component="button"
                          onClick={() => setDeleteConfirm(rule.rule_id)}
                          sx={{
                            background: "none",
                            border: "none",
                            color: "#EF4444",
                          }}
                        >
                          <i className="fa fa-trash" />
                        </MDBox>
                      )}
                    </MDBox>
                  </MDBox>
                ))}
              </MDBox>
            ))
          )}

          {/* Nav */}
          <MDBox mt={4} display="flex" justifyContent="space-between">
            <MDButton
              onClick={() => {
                setStep("select-device");
                setShowRuleForm(false);
                setRuleForm(BLANK_RULE);
              }}
              variant="outlined"
              color="secondary"
              sx={{ gap: 1 }}
            >
              <i className="fa fa-chevron-left" /> Edit
            </MDButton>
            <MDButton
              component={Link}
              to="/onboarding"
              variant="gradient"
              color="info"
              sx={{ gap: 1 }}
            >
              Done
            </MDButton>
          </MDBox>
        </MDBox>
      )}
    </MDBox>
  );
}
